import { useRouter, type UnknownOutputParams } from 'expo-router'
import type { ReactNode } from 'react'

import { EVENT_ORIGIN } from '@/src/features/community/models/community.constants'
import { REWARD_CITATION_TYPE } from '@/src/features/pray/models/rewardCitationType.enum'
import { PrayRewardScreen } from '@/src/features/pray/ui/screens/PrayRewardScreen'
import { useXMRPMRedirect } from '@/src/features/rosario/services/useXMRPMRedirect'
import type { TSourceParam } from '@/src/general/constants/routes'
import { TEST_ID } from '@/src/general/constants/testId'
import { useCanDismiss } from '@/src/general/hooks/useCanDismiss'
import { useSearchParamsAction } from '@/src/general/hooks/useSearchParamsAction'
import { useTrackDisplayed } from '@/src/general/hooks/useTrackOnce'
import { EVENTS, TRACKING_SOURCE } from '@/src/general/utils/gtm/constants'
import { useRoutes } from '@/src/general/utils/routes'

import { usePublicationPrayMutation } from '../../../services/usePublicationPrayMutation'
import { usePublicationQuery } from '../../../services/usePublicationQuery'
import { PublicationRewardScreenContent } from './components/PublicationRewardScreenContent'
import { PublicationRewardSuccessDetails } from './components/PublicationRewardSuccessDetails'

type TPublicationRewardScreenProps = TSourceParam & {
  id: number
  searchParams: UnknownOutputParams
}

export const PublicationRewardScreen = ({ id, source, searchParams }: TPublicationRewardScreenProps): ReactNode => {
  const router = useRouter()
  const { routes } = useRoutes()
  const { canDismissTo } = useCanDismiss()

  const { data: publication, isPending, isError } = usePublicationQuery(id)
  const { mutate: prayPublication } = usePublicationPrayMutation({
    id,
    searchParams,
    communityLifetime: publication?.community.lifetime,
    communityId: publication?.community.id,
    hasJoinedCommunity: publication?.community.hasJoined,
  })

  const destination = routes['prayer-space']()
  const postRewardRedirect = useXMRPMRedirect({ destination })

  const handleContinue = () => {
    const isFromCommunityPublications = searchParams.source === TRACKING_SOURCE.COMMUNITY_PUBLICATIONS_PAGE
    // From the community publications list, the stack is: community > publications list > publication > reward
    const numberScreensToGoBack = isFromCommunityPublications ? 3 : 2
    const shouldRedirectToCommunity =
      searchParams.source === TRACKING_SOURCE.COMMUNITY_JOIN ||
      (isFromCommunityPublications && !canDismissTo(numberScreensToGoBack))

    if (shouldRedirectToCommunity && publication?.community?.id) {
      router.replace(
        routes.community({
          id: publication?.community?.id,
          slug: publication?.community?.slug || '',
          origin: EVENT_ORIGIN.UNDEFINED,
          redirect: 'prayer-space',
        }),
      )
    } else if (canDismissTo(numberScreensToGoBack) && postRewardRedirect === destination) {
      router.dismiss(numberScreensToGoBack)
    } else {
      router.replace(postRewardRedirect)
    }
  }

  useSearchParamsAction({
    searchParams,
    action: () => {
      prayPublication()
    },
    // Disable the action as long as the publication is not fetched so avoid the following issue:
    // 1. GET publication is sent and POST pray is sent
    // 2. GET publication THEN POST pray are handled by the server
    // 3. For some reason, POST pray success is handled first but GET publication cache is not updated with `hasPrayed: true` since it is still empty
    // 4. GET publication success is handled next, and its cache is populated with `hasPrayed: false`
    requestedQuery: publication ? { action: 'mark-as-prayed' } : null,
  })

  useTrackDisplayed(EVENTS.POST_PRAY_PUBLICATION_DISPLAYED, publication?.id, { trackingSource: source })

  return (
    <PrayRewardScreen
      isLoading={isPending}
      rewardType={REWARD_CITATION_TYPE.IMAGE}
      onContinue={handleContinue}
      successDetails={<PublicationRewardSuccessDetails isError={isError} publication={publication} />}
      testID={TEST_ID.PUBLICATION.REWARD.SCREEN.NAME}
    >
      <PublicationRewardScreenContent isError={isError} publication={publication} source={source} />
    </PrayRewardScreen>
  )
}
